// Custom hook to manage the state and logic of the Bézier Pen tool in PatternStudio.
// This encapsulates all the complex state transitions and math for placing anchors,
// dragging handles, and emitting shape updates, allowing the main PatternStudio
// component to remain cleaner and focused on higher-level concerns.
// States:
//   idle      → tool selected, no path in progress. Mouse does NOTHING.
//   placing   → mouseDown held on a new anchor. Drag = pull outHandle.
//   hovering  → mouseUp after placing. Mouse shows rubber-band preview only.
//
// Finishing:
//   double-click  → finish (open path)
//   Enter key     → finish (open path)   ← wired in PatternStudio keyboard handler
//   Escape key    → cancel               ← wired in PatternStudio keyboard handler

import { useCallback, useRef, useState } from 'react';
import type { BezierPenShape, BezierAnchor } from '../types/beizure.types';
import { createAnchor, mirrorHandle } from '../utils/beizerUtlis';

type PenState = 'idle' | 'placing' | 'hovering';

interface UseBezierToolOptions {
  onShapeUpdate: (shape: BezierPenShape) => void;
  onShapeCommit: (shape: BezierPenShape) => void;
  onShapeCancel: (shapeId: string) => void;
  snapToGrid:    (v: number) => number;
}

export function useBezierTool({
  onShapeUpdate,
  onShapeCommit,
  onShapeCancel,
  snapToGrid,
}: UseBezierToolOptions) {

  // ── All mutable draw state lives in refs so callbacks never go stale ────────
  const penStateRef           = useRef<PenState>('idle');
  const committedAnchorsRef   = useRef<BezierAnchor[]>([]);
  const shapeIdRef            = useRef<string | null>(null);
  const mouseDownPosRef       = useRef<{ x: number; y: number } | null>(null);
  const isDraggingHandleRef   = useRef(false);
  // Keep latest callbacks in refs so inner functions always call the current one
  const onShapeUpdateRef      = useRef(onShapeUpdate);
  const onShapeCommitRef      = useRef(onShapeCommit);
  const onShapeCancelRef      = useRef(onShapeCancel);
  const snapToGridRef         = useRef(snapToGrid);

  // Keep callback refs in sync every render (no dependency-array issues)
  onShapeUpdateRef.current  = onShapeUpdate;
  onShapeCommitRef.current  = onShapeCommit;
  onShapeCancelRef.current  = onShapeCancel;
  snapToGridRef.current     = snapToGrid;

  // Only one React state bit — drives the toolbar active indicator
  const [isDrawing, setIsDrawing] = useState(false);

  // ── Pure helpers (no closures over props/state — use refs only) ─────────────

  const toCanvas = (stage: any, screenPos: { x: number; y: number }) =>
    stage.getAbsoluteTransform().copy().invert().point(screenPos);

  const snap = (v: number) => snapToGridRef.current(v);

  /** Emit the current committed anchors + optional ghost preview anchor */
  const emit = (ghostAnchor?: BezierAnchor) => {
    if (!shapeIdRef.current) return;
    const shape: BezierPenShape = {
      id:          shapeIdRef.current,
      type:        'bezierPen',
      anchors:     ghostAnchor
                     ? [...committedAnchorsRef.current, ghostAnchor]
                     : [...committedAnchorsRef.current],
      closed:      false,
      stroke:      '#000000',
      strokeWidth: 2,
      fill:        'transparent',
    };
    onShapeUpdateRef.current(shape);
  };

  /** Reset ALL draw state back to idle — called after commit or cancel */
  const resetState = () => {
    penStateRef.current         = 'idle';
    committedAnchorsRef.current = [];
    shapeIdRef.current          = null;
    mouseDownPosRef.current     = null;
    isDraggingHandleRef.current = false;
  };

  // ── mouseDown ────────────────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e: any, stage: any) => {
    if (!stage || e.evt.button !== 0) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const raw = toCanvas(stage, pointer);
    const x   = snap(raw.x);
    const y   = snap(raw.y);

    mouseDownPosRef.current     = { x, y };
    isDraggingHandleRef.current = false;

    if (penStateRef.current === 'idle') {
      // ── Start new path ───────────────────────────────────────────────────────
      shapeIdRef.current          = `bezierPen-${Date.now()}`;
      committedAnchorsRef.current = [createAnchor(x, y, true)];
      penStateRef.current         = 'placing';
      setIsDrawing(true);
      emit();
      return;
    }

    if (penStateRef.current === 'hovering') {
      // ── Add next anchor ──────────────────────────────────────────────────────
      // inHandle = mirror of previous outHandle → G1-continuous smooth join
      const prev      = committedAnchorsRef.current[committedAnchorsRef.current.length - 1];
      const newAnchor = createAnchor(x, y, true);
      newAnchor.inHandle  = mirrorHandle({ x, y }, prev.outHandle);
      newAnchor.outHandle = { x, y }; // collapsed until user drags

      committedAnchorsRef.current = [...committedAnchorsRef.current, newAnchor];
      penStateRef.current         = 'placing';
      emit();
    }
  }, []); // stable — uses only refs

  // ── mouseMove ────────────────────────────────────────────────────────────────
  const handleMouseMove = useCallback((e: any, stage: any) => {
    // ── HARD GATE: do absolutely nothing when idle ───────────────────────────
    if (penStateRef.current === 'idle') return;
    if (!stage) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const raw = toCanvas(stage, pointer);
    const x   = snap(raw.x);
    const y   = snap(raw.y);

    // ── PLACING: mouse is held — drag out the outHandle ──────────────────────
    if (penStateRef.current === 'placing') {
      const down = mouseDownPosRef.current;
      if (down && Math.hypot(x - down.x, y - down.y) > 3) {
        isDraggingHandleRef.current = true;
      }

      if (isDraggingHandleRef.current) {
        const anchors = committedAnchorsRef.current.slice();
        const last    = { ...anchors[anchors.length - 1] };
        last.outHandle = { x, y };
        last.inHandle  = mirrorHandle(last, last.outHandle); // smooth mirror
        anchors[anchors.length - 1] = last;
        committedAnchorsRef.current = anchors;
        emit();
      }
      return;
    }

    // ── HOVERING: mouse is free — rubber-band preview only ───────────────────
    if (penStateRef.current === 'hovering') {
      const prev  = committedAnchorsRef.current[committedAnchorsRef.current.length - 1];
      const ghost = createAnchor(x, y, true);
      // inHandle mirrors prev outHandle → the preview curve is already smooth
      ghost.inHandle  = mirrorHandle({ x, y }, prev.outHandle);
      ghost.outHandle = { x, y };
      emit(ghost); // ghost is NOT stored in committedAnchorsRef
    }
  }, []); // stable — uses only refs

  // ── mouseUp ──────────────────────────────────────────────────────────────────
  const handleMouseUp = useCallback((_e: any, _stage: any) => {
    if (penStateRef.current !== 'placing') return;

    // If user just clicked (no drag) → collapse handles = clean corner point
    if (!isDraggingHandleRef.current) {
      const anchors = committedAnchorsRef.current.slice();
      const last    = { ...anchors[anchors.length - 1] };
      last.inHandle  = { x: last.x, y: last.y };
      last.outHandle = { x: last.x, y: last.y };
      anchors[anchors.length - 1] = last;
      committedAnchorsRef.current = anchors;
    }

    penStateRef.current         = 'hovering';
    isDraggingHandleRef.current = false;
    mouseDownPosRef.current     = null;
    emit(); // push clean committed state (no ghost)
  }, []); // stable

  // ── finishPath ───────────────────────────────────────────────────────────────
  const finishPath = useCallback((closed = false) => {
    const anchors = committedAnchorsRef.current;
    const id      = shapeIdRef.current;

    if (!id || anchors.length < 2) {
      // Not enough anchors — just cancel silently
      resetState();
      setIsDrawing(false);
      if (id) onShapeCancelRef.current(id);
      return;
    }

    const finalShape: BezierPenShape = {
      id,
      type:        'bezierPen',
      anchors:     [...anchors], // committed only, no ghost
      closed,
      stroke:      '#000000',
      strokeWidth: 2,
      fill:        'transparent',
    };

    resetState();
    setIsDrawing(false);
    onShapeCommitRef.current(finalShape);
  }, []); // stable

  // ── cancelPath ───────────────────────────────────────────────────────────────
  const cancelPath = useCallback(() => {
    const id = shapeIdRef.current;
    resetState();
    setIsDrawing(false);
    if (id) onShapeCancelRef.current(id);
  }, []); // stable

  // ── handleDblClick ───────────────────────────────────────────────────────────
  // Browser fires: mouseDown→mouseUp→mouseDown→mouseUp→dblClick
  // The second mouseDown already added a duplicate anchor → strip it first.
  const handleDblClick = useCallback((e: any, _stage: any) => {
    e.evt?.stopPropagation?.();
    if (penStateRef.current === 'idle') return;

    // Strip the phantom anchor added by the second click of the double-click
    if (committedAnchorsRef.current.length > 1) {
      committedAnchorsRef.current = committedAnchorsRef.current.slice(0, -1);
    }

    finishPath(false);
  }, [finishPath]);

  return {
    isDrawing,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleDblClick,
    finishPath,
    cancelPath,
    getCurrentShape: () => {
      if (!shapeIdRef.current) return null;
      return {
        id:          shapeIdRef.current,
        type:        'bezierPen' as const,
        anchors:     [...committedAnchorsRef.current],
        closed:      false,
        stroke:      '#000000',
        strokeWidth: 2,
        fill:        'transparent',
      };
    },
  };
}